import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import { ApolloProvider } from '@apollo/client';
import {
  rolesNPermissionsRoutes,
  ROUTE_ADMIN,
  ROUTE_AVATAR_UPLOAD,
  PRODUCT_COURSE_MAPPING,
  ROUTE_MEMBERS,
  ROUTE_ORGANISATIONS,
  ROUTE_RNP,
  ESIS_JOBS,
} from './routeHelpers';
import NavigationMenu, {
  NAV_MENU_COLLAPSED_WIDTH,
} from '../NavigationMenu/NavigationMenu';
import TopBar, { TOP_BAR_HEIGHT } from '../TopBar/TopBar';
import Organisations from '../../pages/Organisations';
import AddMember from '../../pages/AddMember/AddMember';
import EditMember from '../../pages/EditMember/EditMember';
import getApolloClient, {
  AUTHORING_AGGREGATOR_ENDPOINT,
  MARKETPLACE_GATEWAY,
  ORGANISATION_SERVICE_ENDPOINT,
} from '../../apollo/getApolloClient';
import Members from '../../pages/Members/Members';
import RolesAndPermissions from '../../pages/RolesAndPermissions/RolesAndPermissions';
import isInCCLiframe from '../../utils/isInCCLiframe';
import { CreateNewRole } from '../RolesAndPermissions';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import { RNPTabs } from '../../pages/RolesAndPermissions/types';
import AdminUsers from '../../pages/AdminUsers/AdminUsers';
import EditAdminUser from '../../pages/EditAdminUser/EditAdminUser';
import Loading from '../Loading';
import AddAdminUser from '../../pages/AddAdminUser/AddAdminUser';
import OrganisationMembersTabView from '../../pages/OrganisationMembers/OrganisationMembersTabView';
import { OrgMembersViewTabs } from '../../pages/OrganisationMembers/types';
import AvatarUpload from '../../pages/AvatarUpload/AvatarUpload';
import ProductCourseMapping from '../../pages/ProductCourseMapping/ProductCourseMapping';
import BulkUpload from '../../pages/BulkUpload/BulkUpload';
import EsisLogs from '../../pages/EsisLogs/EsisLogs';
import EsisJobDetail from '../../pages/EsisLogs/EsisJobDetail';

const PAGE_VIEW_EXPANDED_MARGIN_LEFT = 280;

const getPageViewMarginLeft = (isNavCollapsed: boolean, space: any) =>
  isNavCollapsed
    ? NAV_MENU_COLLAPSED_WIDTH + space[40]
    : PAGE_VIEW_EXPANDED_MARGIN_LEFT;

const StyledPageView = styled.div<{ isNavCollapsed: boolean }>`
  width: ${({ theme: { space }, isNavCollapsed }: any) => {
    if (isInCCLiframe) return '90%';
    const freedWidth =
      PAGE_VIEW_EXPANDED_MARGIN_LEFT -
      getPageViewMarginLeft(isNavCollapsed, space);
    return `calc(80% + ${freedWidth}px)`;
  }};
  margin: ${({ theme: { space }, isNavCollapsed }: any) =>
    isInCCLiframe
      ? `${space[24]}px auto ${space[24]}px auto`
      : `${TOP_BAR_HEIGHT + space[24]}px ${space[24]}px ${
          space[80]
        }px ${getPageViewMarginLeft(isNavCollapsed, space)}px`};
  transition: margin 0.2s ease-in-out, width 0.2s ease-in-out;
`;

const NAV_MENU_COLLAPSED_KEY = 'navMenuCollapsed';

const getParam = (name: string) =>
  new URLSearchParams((window as any).location.search).get(name);

const AppRoutes = () => {
  const [token, setToken] = useState(getParam('token'));
  const [isNavCollapsed, setIsNavCollapsed] = useState(
    () => localStorage.getItem(NAV_MENU_COLLAPSED_KEY) === 'true',
  );

  const toggleNavMenu = () => {
    const nextIsCollapsed = !isNavCollapsed;
    localStorage.setItem(NAV_MENU_COLLAPSED_KEY, String(nextIsCollapsed));
    setIsNavCollapsed(nextIsCollapsed);
  };

  const authoringClient = useMemo(
    () => getApolloClient(AUTHORING_AGGREGATOR_ENDPOINT, true, token),
    [token],
  );
  const organisationClient = useMemo(
    () => getApolloClient(ORGANISATION_SERVICE_ENDPOINT, true, token),
    [token],
  );
  const marketplaceClient = useMemo(
    () => getApolloClient(MARKETPLACE_GATEWAY, true, token),
    [token],
  );

  useEffect(() => {
    window.addEventListener('message', function (e) {
      const data = e.data;
      if (e.origin.match(/[^.\s]+\.alefed\.com/g) && data.token) {
        setToken(data.token);
      }
    });
  }, []);

  const getOrganisationMembersTab = (currentTab: OrgMembersViewTabs) => {
    const tab = () => <OrganisationMembersTabView currentTab={currentTab} />;
    const isAuthorized = token ? tab() : <Loading />;
    return () => (isInCCLiframe ? isAuthorized : tab());
  };

  return (
    <Router>
      {!isInCCLiframe && (
        <>
          <NavigationMenu
            isCollapsed={isNavCollapsed}
            onToggle={toggleNavMenu}
          />
          <TopBar isNavCollapsed={isNavCollapsed} />
        </>
      )}

      <StyledPageView isNavCollapsed={isNavCollapsed}>
        <Switch>
          <Route exact path="/" component={Organisations} />
          <Route
            exact
            path={ROUTE_ORGANISATIONS.organisations}
            component={Organisations}
          />
          <ApolloProvider client={authoringClient}>
            <Route exact path={ROUTE_MEMBERS.members} component={Members} />
            <Route
              exact
              path={ROUTE_ORGANISATIONS.organisationMembers}
              render={getOrganisationMembersTab(OrgMembersViewTabs.Members)}
            />

            <ApolloProvider client={organisationClient}>
              <Route
                exact
                path={ROUTE_ORGANISATIONS.organisationSettings}
                render={getOrganisationMembersTab(OrgMembersViewTabs.Settings)}
              />
            </ApolloProvider>
            <Route
              exact
              path={ROUTE_ORGANISATIONS.addMember}
              render={() => <AddMember isOrgMember />}
            />
            <Route
              exact
              path={ROUTE_ORGANISATIONS.editMember}
              render={() => <EditMember isOrgMember />}
            />
            <Route
              exact
              path={ROUTE_MEMBERS.addMember}
              render={() => <AddMember />}
            />
            <Route
              exact
              path={ROUTE_MEMBERS.editMember}
              render={() => <EditMember />}
            />

            {rolesNPermissionsRoutes.map(({ path, currentTab }: any, i) => (
              <Route
                key={i}
                exact
                path={path}
                render={() => <RolesAndPermissions currentTab={currentTab} />}
              />
            ))}

            {FEATURE_TOGGLE.RNP_CUSTOM_ROLES && (
              <React.Fragment>
                <Route
                  exact
                  path={ROUTE_RNP.createNewRole}
                  component={CreateNewRole}
                />
                <Route
                  exact
                  path={ROUTE_RNP.updateCustomRole}
                  render={() => (
                    <CreateNewRole pageToEdit={RNPTabs.CUSTOM_ROLES} />
                  )}
                />
              </React.Fragment>
            )}

            {FEATURE_TOGGLE.ADMINS_TOGGLE && (
              <React.Fragment>
                <Route
                  exact
                  path={ROUTE_ADMIN.adminsPage}
                  component={AdminUsers}
                />
                <Route
                  exact
                  path={ROUTE_ADMIN.edit}
                  component={EditAdminUser}
                />
                <Route exact path={ROUTE_ADMIN.add} component={AddAdminUser} />
              </React.Fragment>
            )}

            <ApolloProvider client={organisationClient}>
              <Route exact path={ESIS_JOBS.esisLogs} component={EsisLogs} />
              <Route
                exact
                path={ESIS_JOBS.esisLogsDetail}
                component={EsisJobDetail}
              />
              <Route
                exact
                path={PRODUCT_COURSE_MAPPING.productCourseMapping}
                component={ProductCourseMapping}
              />
              <Route
                exact
                path={PRODUCT_COURSE_MAPPING.bulkUpload}
                component={BulkUpload}
              />
            </ApolloProvider>

            {FEATURE_TOGGLE.ENABLE_AVATAR_UPLOAD && (
              <ApolloProvider client={marketplaceClient}>
                <Route
                  exact
                  path={ROUTE_AVATAR_UPLOAD.avatarUpload}
                  component={AvatarUpload}
                />
              </ApolloProvider>
            )}
          </ApolloProvider>
        </Switch>
      </StyledPageView>
    </Router>
  );
};

export default AppRoutes;
